import { API_BASE } from './base'
import type {
  MarketSnapshot,
  PlaceTradingOrder,
  TradingAccount,
  TradingFill,
  TradingOrder,
} from '@/types/paperTrading'

async function request<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, init)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const detail = (body as { detail?: string | { message?: string } }).detail
    throw new Error(
      typeof detail === 'string' ? detail : detail?.message ?? `HTTP ${res.status}`,
    )
  }
  return res.json() as Promise<T>
}

export const getTradingAccount = (portfolioId: number): Promise<TradingAccount> =>
  request(`${API_BASE}/portfolios/${portfolioId}/trading/account`)

export const getTradingQuotes = (
  portfolioId: number,
  symbols: string[],
): Promise<{ items: MarketSnapshot[] }> => {
  const query = new URLSearchParams()
  symbols.forEach(symbol => query.append('symbols', symbol))
  return request(`${API_BASE}/portfolios/${portfolioId}/trading/quotes?${query}`)
}

export const getTradingOrders = (
  portfolioId: number,
  options?: {
    status?: string
    page?: number
    size?: number
    filledOnly?: boolean
  },
): Promise<{ items: TradingOrder[]; total: number; page: number; size: number }> => {
  const query = new URLSearchParams()
  if (options?.status) query.set('status', options.status)
  if (options?.page != null) query.set('page', String(options.page))
  if (options?.size != null) query.set('size', String(options.size))
  if (options?.filledOnly) query.set('filled_only', 'true')
  const qs = query.toString()
  return request(`${API_BASE}/portfolios/${portfolioId}/trading/orders${qs ? `?${qs}` : ''}`)
}

export const placeTradingOrder = (
  portfolioId: number,
  data: PlaceTradingOrder,
): Promise<TradingOrder> =>
  request(`${API_BASE}/portfolios/${portfolioId}/trading/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

export const cancelTradingOrder = (
  portfolioId: number,
  orderId: string,
): Promise<{ status: string }> =>
  request(`${API_BASE}/portfolios/${portfolioId}/trading/orders/${orderId}/cancel`, {
    method: 'POST',
  })

export const modifyTradingOrder = (
  portfolioId: number,
  orderId: string,
  data: PlaceTradingOrder,
): Promise<TradingOrder> =>
  request(`${API_BASE}/portfolios/${portfolioId}/trading/orders/${orderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

export const getTradingFills = (
  portfolioId: number,
  orderId: string,
): Promise<{ items: TradingFill[] }> =>
  request(`${API_BASE}/portfolios/${portfolioId}/trading/orders/${orderId}/fills`)

export const changeTradingCash = (
  portfolioId: number,
  action: 'deposit' | 'withdraw',
  currency: string,
  amount: string,
): Promise<{ status: string }> =>
  request(`${API_BASE}/portfolios/${portfolioId}/trading/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currency, amount }),
  })
